<script lang="ts">
	import LoadMorePosts from './LoadMorePosts.svelte';
	import '../stream.css';
	import BreakPoint from '$lib/components/BreakPoint/BreakPoint.svelte';
	import Activities from './Activities/Activities.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import PostBox from './PostBox/PostBox.svelte';
	import type { Page } from '$lib/api/rpc';
	import Carousel from './Carousel/Carousel.svelte';
	import ScreenSize from '$lib/utils/ScreenSizes';
	import { atLeastCollaborator, atLeastModerator, atLeastOwner, type Role } from '$lib/utils/roles';
	import { browser } from '$app/environment';
	import { page } from '$app/stores';
	import { derived } from 'svelte/store';
	import Tile from '$lib/components/Post/Tile/Tile.svelte';
	import WidgetTile from '$lib/components/Post/Tile/WidgetTile.svelte';
	import { isWidget } from '$lib/components/WidgetModal/widgetHelpers';
	import { getPostsForCurrentStream } from './helpers/getPostsForCurrentStream';

	export let nextPage: Page | undefined;

	const { hub, collections, featuredPosts, pinnedPosts, posts } = getHubStores();

	const coverHideCover = $hub.theme?.coverHideCover;

	let currentCollection = derived(
		page,
		($page) =>
			$collections.find(
				(collection) => collection.slug == $page.data.postsFilters?.collectionSlug
			) || $collections[0]
	);

	let currentTag = derived(page, ($page) => $page.data.postsFilters?.tag);

	/**
	 * @description All posts in the current collection, filtered based on any tag filter applied.
	 * Note that filteredPosts includes all featured and pinned posts as well.
	 */
	let filteredPosts = derived([posts, currentTag], ([$posts, $currentTag]) => {
		if ($currentTag) {
			return $posts.filter((post) => post.tags.includes($currentTag));
		}

		return $posts;
	});

	//TODO: move function out to helpers directory and write tests for it
	// Note that if there is a tag filter applied, we are not filtering based on the collection as well.
	// This seems incorrect.
	// e.g user is on the `widgets` collection and selects the `#touchpoint` tag,
	// using this logic all posts with the `#touchpoint` tag would be shown, regardless
	// of whether or not they are in the `widgets` collection. Same is true in the case of filteredPinnedPosts
	let filteredFeaturedPosts = derived(
		[featuredPosts, currentCollection, currentTag],
		([$featuredPosts, $currentCollection, $currentTag]) => {
			// currentTag is not undefined if a hashtag is selected
			if ($currentTag) {
				return $featuredPosts.filter((post) => post.tags.includes($currentTag));
			}
			// $currentCollection.id == null is the 'All' collection, injected in [hubPrefix=hubPrefixMatcher]/+layout.ts
			if ($currentCollection.id == null) {
				// Filter out featured posts that should be hidden from the "All" collection
				return $featuredPosts.filter((post) => !post.hide_all);
			}
			return $featuredPosts.filter((post) => post.collection?.id === $currentCollection.id);
		}
	);

	//TODO: move function out to helpers directory and write tests for it
	let filteredPinnedPosts = derived(
		[pinnedPosts, currentCollection, currentTag],
		([$pinnedPosts, $currentCollection, $currentTag]) => {
			// currentTag is not undefined if a hashtag is selected
			if ($currentTag) {
				return $pinnedPosts.filter((post) => post.tags.includes($currentTag));
			}
			// $currentCollection.id == null is the 'all' collection, injected in [hubPrefix=hubPrefixMatcher]/+layout.ts
			if ($currentCollection.id == null) {
				// Filter out pinned posts that should be hidden from the "All" collection
				return $pinnedPosts.filter((pinnedPost) => !pinnedPost.hide_all);
			}
			return $pinnedPosts.filter((post) => post.collection?.id === $currentCollection.id);
		}
	);

	let postsForCurrentStream = derived(
		[filteredPosts, filteredFeaturedPosts, filteredPinnedPosts],
		([$filteredPosts, $filteredFeaturedPosts, $filteredPinnedPosts]) => {
			return getPostsForCurrentStream($filteredPosts, $filteredFeaturedPosts, $filteredPinnedPosts);
		}
	);
	let innerWidth: number;

	//TODO: move function out to helpers directory and write tests for it
	$: canPostInCurrentCollection = () => {
		const hasPermission = (role: Role) => {
			if (role == 'member') {
				return $hub.user_context.perms.posts.submit;
			} else {
				return $hub.user_context.perms.posts.publish;
			}
		};

		if ($page.data.postsFilters?.collectionSlug == '') {
			// meaning the "All" collection is selected
			return hasPermission($hub.user_context.hub_role as Role);
		} else {
			if ($currentCollection?.min_role == 'member') {
				return hasPermission($hub.user_context.hub_role as Role);
			} else if (
				$currentCollection?.min_role == 'collaborator' &&
				atLeastCollaborator($hub.user_context)
			) {
				return $hub.user_context.perms.posts.publish;
			} else if (
				$currentCollection?.min_role == 'moderator' &&
				atLeastModerator($hub.user_context)
			) {
				return $hub.user_context.perms.posts.publish;
			} else if ($currentCollection?.min_role == 'owner' && atLeastOwner($hub.user_context)) {
				return $hub.user_context.perms.posts.publish;
			} else {
				return false;
			}
		}
	};
</script>

<svelte:window bind:innerWidth />

<div class="content" class:coverHideCover>
	<BreakPoint>
		<main>
			{#if currentCollection && canPostInCurrentCollection()}
				<PostBox currentCollection={$currentCollection} />
			{/if}
			{#if innerWidth > ScreenSize.mediumScreen && $filteredFeaturedPosts.length && browser}
				<Carousel posts={filteredFeaturedPosts} />
			{/if}
			<div class="postsContainer">
				{#if innerWidth < ScreenSize.mediumScreen && $filteredFeaturedPosts.length}
					{#each $filteredFeaturedPosts as post, postIndex (post.id)}
						<Tile {post} {postIndex} parent="collectionPage" />
					{/each}
				{/if}
				<Activities />
				{#each $postsForCurrentStream as post, postIndex (post.id)}
					{#if isWidget(post.type)}
						<WidgetTile {post} {postIndex} />
					{:else}
						<Tile {post} {postIndex} parent="collectionPage" />
					{/if}
				{/each}
			</div>
			<LoadMorePosts {nextPage} />
		</main>
	</BreakPoint>
</div>

<style>
	.content {
		display: flex;
		position: relative;
		align-items: center;
		justify-content: center;
		background-color: var(--streamTileBackdropColor);
	}

	.coverHideCover {
		margin-block-start: 50px; /* For some reason we need to add a margin if the cover is hidden */
	}

	.postsContainer {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		justify-content: flex-start;
		inline-size: 100%;
	}
</style>
